import { LoadingSpinner } from "@connected-repo/ui-mui/components/LoadingSpinner";
import { Chip } from "@connected-repo/ui-mui/data-display/Chip";
import { Typography } from "@connected-repo/ui-mui/data-display/Typography";
import { Button } from "@connected-repo/ui-mui/form/Button";
import { Box } from "@connected-repo/ui-mui/layout/Box";
import { Card } from "@connected-repo/ui-mui/layout/Card";
import { Grid } from "@connected-repo/ui-mui/layout/Grid";
import { Stack } from "@connected-repo/ui-mui/layout/Stack";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TuneIcon from "@mui/icons-material/Tune";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { AdjustMinutesDialog } from "@superadmin/components/AdjustMinutesDialog";
import { GrantMinutesDialog } from "@superadmin/components/GrantMinutesDialog";
import { MinutesLedgerTable } from "@superadmin/components/MinutesLedgerTable";
import { RoomCapacityCard } from "@superadmin/components/RoomCapacityCard";
import { SubdomainCard } from "@superadmin/components/SubdomainCard";
import { absoluteDateTime, formatDate, formatMinutes } from "@superadmin/utils/format";
import { orpc } from "@superadmin/utils/orpc.client";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";

const Stat = ({ label, value, color }: { label: string; value: string; color?: string }) => (
	<Card sx={{ p: 2.5, borderRadius: 2, border: "1px solid", borderColor: "divider", height: "100%" }}>
		<Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>
			{label}
		</Typography>
		<Typography variant="h4" sx={{ fontWeight: 800, color: color ?? "text.primary" }}>
			{value}
		</Typography>
	</Card>
);

export default function TeamDetailPage() {
	const { teamId = "" } = useParams<{ teamId: string }>();
	const navigate = useNavigate();
	const [grantOpen, setGrantOpen] = useState(false);
	const [adjustOpen, setAdjustOpen] = useState(false);

	const { data, isLoading, error } = useQuery({
		...orpc.getTeam.queryOptions({ input: { teamId } }),
		enabled: !!teamId,
	});

	if (isLoading) return <LoadingSpinner text="Loading team…" />;
	if (error || !data) {
		return (
			<Stack spacing={2}>
				<Typography color="error">{error?.message ?? "Team not found"}</Typography>
				<Button onClick={() => navigate("/teams")}>Back to teams</Button>
			</Stack>
		);
	}

	const { team, members, minutes, rooms } = data;

	return (
		<Stack spacing={3}>
			<Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/teams")} sx={{ alignSelf: "flex-start", color: "text.secondary" }}>
				Teams
			</Button>

			<Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
				<Box>
					<Stack direction="row" spacing={1} alignItems="center">
						<Typography variant="h4" sx={{ fontWeight: 800 }}>
							{team.name}
						</Typography>
						{team.personalTeamForUserId && <Chip label="Personal" size="small" variant="outlined" />}
					</Stack>
					<Typography color="text.secondary">
						{team.slug ? `${team.slug} · ` : ""}Created {formatDate(team.createdAt)}
						{team.appName ? ` · App name "${team.appName}"` : ""}
					</Typography>
				</Box>
				<Stack direction="row" spacing={1} alignItems="flex-start">
					<Button variant="contained" startIcon={<AddIcon />} onClick={() => setGrantOpen(true)} sx={{ minHeight: 44, borderRadius: 2 }}>
						Assign minutes
					</Button>
					<Button variant="outlined" startIcon={<TuneIcon />} onClick={() => setAdjustOpen(true)} sx={{ minHeight: 44, borderRadius: 2 }}>
						Adjust
					</Button>
				</Stack>
			</Stack>

			<Grid container spacing={2}>
				<Grid size={{ xs: 6, md: 3 }}>
					<Stat label="Balance" value={formatMinutes(minutes.balance)} color={minutes.balance > 0 ? "success.main" : "error.main"} />
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<Stat label="Purchased" value={formatMinutes(minutes.totalGranted)} />
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<Stat label="Used" value={formatMinutes(minutes.totalUsed)} />
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<Stat label="Adjustments" value={formatMinutes(minutes.totalAdjusted)} />
				</Grid>
			</Grid>

			<SubdomainCard teamId={team.id} slug={team.slug} />

			<RoomCapacityCard key={rooms.capacity} teamId={team.id} rooms={rooms} />

			<Box>
				<Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
					Minutes history
				</Typography>
				<MinutesLedgerTable entries={minutes.entries} showActor />
			</Box>

			<Box>
				<Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
					Members ({members.length})
				</Typography>
				<Card sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", overflowX: "auto" }}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Name</TableCell>
								<TableCell>Email / phone</TableCell>
								<TableCell>Role</TableCell>
								<TableCell>Joined</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{members.map((m) => (
								<TableRow key={m.id}>
									<TableCell>{m.userName ?? <Typography color="text.disabled">Invited</Typography>}</TableCell>
									<TableCell>{m.email ?? m.phoneNumber ?? "—"}</TableCell>
									<TableCell>
										<Chip label={m.role} size="small" color={m.role === "Owner" ? "primary" : "default"} variant="outlined" />
									</TableCell>
									<TableCell title={absoluteDateTime(m.joinedAt)}>{formatDate(m.joinedAt)}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Card>
			</Box>

			{grantOpen && <GrantMinutesDialog open teamId={team.id} teamName={team.name} onClose={() => setGrantOpen(false)} />}
			{adjustOpen && (
				<AdjustMinutesDialog open teamId={team.id} teamName={team.name} balance={minutes.balance} onClose={() => setAdjustOpen(false)} />
			)}
		</Stack>
	);
}
